<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationsAPI from 'dashboard/api/inbox/conversation';
import ConversationAPI from 'dashboard/api/conversations';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();

const props = defineProps({
  label: { type: Object, required: true },
  active: { type: Boolean, default: false },
});

const count = ref(0);
const loading = ref(false);
const isDragOver = ref(false);
let intervalId = null;

const fetchConversationCount = async () => {
  if (!props.label?.title) {
    return;
  }

  try {
    loading.value = true;

    const params = {
      status: 'open',
      labels: [props.label.title],
    };

    const response = await ConversationsAPI.meta(params);
    count.value = response.data?.meta?.all_count || 0;
  } catch (error) {
    count.value = 0;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchConversationCount();
  intervalId = setInterval(fetchConversationCount, 60000);
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
  }
});

const showBadge = computed(() => true);

// Drag and Drop handlers
const onDragOver = e => {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'copy';
};

const onDragEnter = e => {
  e.preventDefault();
  isDragOver.value = true;
};

const onDragLeave = e => {
  e.preventDefault();
  isDragOver.value = false;
};

const onDrop = async e => {
  e.preventDefault();
  isDragOver.value = false;

  try {
    const data = JSON.parse(e.dataTransfer.getData('application/json'));
    const { conversationId, currentLabels } = data;

    if (!conversationId) {
      return;
    }

    // Verifica se a label já está aplicada
    if (currentLabels.includes(props.label.title)) {
      useAlert(t('CONVERSATION.LABEL_ALREADY_APPLIED', { label: props.label.title }));
      return;
    }

    // Adiciona a nova label às labels existentes
    const updatedLabels = [...currentLabels, props.label.title];

    await ConversationAPI.updateLabels(conversationId, updatedLabels);

    useAlert(t('CONVERSATION.LABEL_APPLIED_SUCCESS', { label: props.label.title }));

    // Atualiza a contagem após aplicar a label
    fetchConversationCount();
  } catch (error) {
    useAlert(t('CONVERSATION.LABEL_APPLIED_ERROR'));
  }
};
</script>

<template>
  <div
    class="flex items-center gap-2 w-full min-w-0 p-1 -m-1 rounded-md transition-all duration-200"
    :class="{
      'bg-n-blue-4 dark:bg-n-blue-5 ring-2 ring-n-blue-8 scale-[1.02]': isDragOver,
    }"
    @dragover.stop.prevent="onDragOver"
    @dragenter.stop.prevent="onDragEnter"
    @dragleave.stop="onDragLeave"
    @drop.stop.prevent="onDrop"
  >
    <span
      class="size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm flex-shrink-0"
      :style="{ backgroundColor: label.color }"
    />
    <div class="flex-1 truncate min-w-0">{{ label.title }}</div>
    <span
      v-if="showBadge"
      class="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full min-w-[1.25rem] text-center leading-none bg-blue-600 text-orange-500"
      :title="`${count} conversas abertas`"
    >
      {{ count > 99 ? '99+' : count }}
    </span>
  </div>
</template>
